import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { useUnistyles, StyleSheet } from 'react-native-unistyles';
import { LegendList } from '@legendapp/list';
import { useRouter } from 'expo-router';
import { parseTimeFrame } from '@/utils/dater';
import Avatar from '@/components/People/Avatar';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { getSubjectTypeColor, getVerbIcon } from './createActivitySummary';
import { formatSubjectType } from '@/utils/buildActivityDetails';

/**
 * ActivityFeed - Shared component for displaying user activity
 * Modern timeline layout with vertical connecting line, avatar, and card
 * 
 * @param {Object} props
 * @param {Object} props.query - React Query result from useInfiniteQuery
 * @param {Function} props.onActivityPress - Callback when activity row is pressed
 * @param {string} props.emptyMessage - Message when no activities
 * @param {Function} props.onEndReached - Additional callback when end reached
 */

const getPastTense = (verb) => {
    const pastTenses = {
        create: 'created',
        update: 'updated',
        delete: 'deleted',
        assign: 'assigned',
        unassign: 'unassigned',
        publish: 'published',
        close: 'closed',
        reopen: 'reopened',
        archive: 'archived',
        unarchive: 'unarchived',
    };
    return pastTenses[verb] || `${verb}d`;
};

/**
 * Parse a single detail line and wrap old/new values in bold accent-colored text.
 */
const parseDetailLine = (line, colors, fonts) => {
    const valueStyle = { fontFamily: fonts.bold, color: colors.primary };

    // Pattern 1: "- Label was updated from OLD to NEW"
    const fromToMatch = line.match(/^- (.+?) was updated from (.+?) to (.+)$/);
    if (fromToMatch) {
        const [, label, oldVal, newVal] = fromToMatch;
        return (
            <Text style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body }}>
                {'- '}
                {label}
                {' was updated from '}
                <Text style={valueStyle}>{oldVal}</Text>
                {' to '}
                <Text style={valueStyle}>{newVal}</Text>
            </Text>
        );
    }

    // Pattern 2: "- Label was updated to NEW"
    const toMatch = line.match(/^- (.+?) was updated to (.+)$/);
    if (toMatch) {
        const [, label, newVal] = toMatch;
        return (
            <Text style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body }}>
                {'- '}
                {label}
                {' was updated to '}
                <Text style={valueStyle}>{newVal}</Text>
            </Text>
        );
    }

    // Pattern 3: "- Label was cleared"
    const clearedMatch = line.match(/^- (.+?) was cleared$/);
    if (clearedMatch) {
        const [, label] = clearedMatch;
        return (
            <Text style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body }}>
                {'- '}{label}{' was cleared'}
            </Text>
        );
    }

    // Fallback: plain text (associative changes like "- Added role 'Member'")
    return (
        <Text style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body }}>
            {line}
        </Text>
    );
};

const ActivityFeed = ({
    query,
    onActivityPress,
    emptyMessage = 'No activity yet',
    onEndReached,
}) => {
    const router = useRouter();
    const { theme } = useUnistyles();
    const { colors, fonts } = theme;
    const { data: loggedInUser } = Query.people.loggedInUser();
    const canViewContributions = loggedInUser?.can?.('View Contributions');

    // Flatten all pages into single array
    const activities = useMemo(() => {
        if (!query.data?.pages) return [];
        const all = query.data.pages.flatMap(page => page.activities);
        // Hide Contribution/Batch entries unless user has 'View Contributions' permission
        if (canViewContributions) return all;
        return all.filter(a => a.subjectType !== 'Contribution' && a.subjectType !== 'Batch');
    }, [query.data, canViewContributions]);

    const hasNextPage = query.hasNextPage;
    const isFetchingNextPage = query.isFetchingNextPage;

    const handleEndReached = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            query.fetchNextPage();
        }
        onEndReached?.();
    }, [hasNextPage, isFetchingNextPage, query, onEndReached]);

    const handleRefresh = useCallback(() => {
        query.refetch();
    }, [query]);

    const renderActivityItem = useCallback(({ item, index }) => {
        const {
            actorPersonID,
            actorFirstName,
            actorLastName,
            actorAvatarStorageKey,
            actorAvatarUpdatedAt,
            verb,
            subjectType,
            subjectID,
            subjectName,
            details,
            occurredAt,
        } = item;

        const timeAgo = parseTimeFrame(occurredAt, true);
        const isDeleted = verb === 'delete';
        const moduleName = getSubjectTypeColor(subjectType);
        const verbIcon = getVerbIcon(verb);
        const isLast = index === activities.length - 1;

        const actorName = actorFirstName && actorLastName
            ? `${actorFirstName} ${actorLastName}`
            : actorFirstName || actorLastName || 'Someone';

        const goToActor = () => {
            if (actorPersonID) {
                router.push(`/people/${actorPersonID}`);
            }
        };

        const goToSubject = () => {
            if (isDeleted || !subjectID || !onActivityPress) return;
            onActivityPress(item);
        };

        // Build actor profile object for Avatar component
        const actorProfile = {
            id: actorPersonID,
            firstName: actorFirstName,
            lastName: actorLastName,
            avatarStorageKey: actorAvatarStorageKey,
            avatarUpdatedAt: actorAvatarUpdatedAt,
        };

        return (
            <View style={styles.item}>
                {/* Timeline column: avatar + vertical line */}
                <View style={styles.timelineColumn}>
                    <Pressable onPress={goToActor}>
                        <Avatar
                            profile={actorProfile}
                            size={36}
                            disableBorder
                        />
                    </Pressable>
                    {/* Vertical line connecting to next item */}
                    {!isLast && (
                        <View style={styles.timelineLine} />
                    )}
                </View>

                {/* Content card */}
                <View style={styles.contentCard}>
                    {/* Summary line: bold actor name + action + subject chip */}
                    <View style={styles.summaryRow}>
                        <Pressable onPress={goToActor}>
                            <Text style={styles.actorName}>
                                {actorName}
                            </Text>
                        </Pressable>

                        <Text style={styles.actionText}>
                            {subjectType === 'SmallGroupSession'
                                ? ` ${getPastTense(verb)} a session in `
                                : ` ${getPastTense(verb)} the ${formatSubjectType(subjectType)} `}
                        </Text>

                        <Pressable
                            onPress={goToSubject}
                            disabled={isDeleted || !onActivityPress}
                            style={({ pressed }) => ({
                                opacity: pressed ? 0.7 : 1,
                            })}
                        >
                            <View style={styles.chip(moduleName)}>
                                <Unicon
                                    name={verbIcon}
                                    size={12}
                                    colorVariant={moduleName}
                                />
                                <Text style={styles.chipText(moduleName)}>
                                    {subjectName || subjectType}
                                </Text>
                            </View>
                        </Pressable>
                    </View>

                    {/* Time on second line */}
                    <Text style={styles.timeText}>
                        {timeAgo}
                    </Text>

                    {/* Details card */}
                    {details && (
                        <View style={styles.detailsCard}>
                            {(() => {
                                const lines = details.split('\n');
                                return lines.map((line, i) => (
                                    <React.Fragment key={i}>
                                        {parseDetailLine(line, colors, fonts)}
                                        {i < lines.length - 1 && <View style={{ height: 2 }} />}
                                    </React.Fragment>
                                ));
                            })()}
                        </View>
                    )}

                    {/* Deleted badge */}
                    {isDeleted && (
                        <View style={styles.deletedBadge}>
                            <View style={styles.deletedBadgeInner}>
                                <Text style={styles.deletedBadgeText}>
                                    Deleted
                                </Text>
                            </View>
                        </View>
                    )}
                </View>
            </View>
        );
    }, [colors, fonts, onActivityPress, activities.length, router]);

    const renderFooter = useCallback(() => {
        if (!isFetchingNextPage) return null;
        return (
            <View style={styles.footer}>
                <ActivityIndicator size="small" color={colors.primary} />
            </View>
        );
    }, [isFetchingNextPage, colors.primary]);

    const renderEmpty = useCallback(() => {
        if (query.isLoading) return null;
        return (
            <View style={styles.empty}>
                <Text style={styles.emptyText}>
                    {emptyMessage}
                </Text>
            </View>
        );
    }, [query.isLoading, emptyMessage, colors.textSecondary]);

    return (
        <View style={styles.container}>
            <LegendList
                data={activities}
                renderItem={renderActivityItem}
                keyExtractor={(item) => item.id}
                estimatedItemSize={100}
                onEndReached={handleEndReached}
                onEndReachedThreshold={0.5}
                ListFooterComponent={renderFooter}
                ListEmptyComponent={renderEmpty}
                refreshing={query.isRefetching}
                onRefresh={handleRefresh}
                recycleItems
                maintainVisibleContentPosition
                keyboardShouldPersistTaps="handled"
            />
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
        alignSelf: 'center',
        width: '100%',
    },
    item: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 4,
    },
    timelineColumn: {
        alignItems: 'center',
        width: 36,
    },
    timelineLine: {
        width: 2,
        flex: 1,
        backgroundColor: colors.border || colors.surface,
        marginTop: 6,
        alignSelf: 'center',
        zIndex:10
    },
    contentCard: {
        flex: 1,
        marginLeft: 14,
        marginBottom: 16,
    },
    summaryRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    actorName: {
        fontSize: 13,
        color: colors.text,
        fontFamily: fonts.bold,
    },
    actionText: {
        fontSize: 13,
        color: colors.text,
        fontFamily: fonts.light,
    },
    chip: (moduleName) => ({
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: colors[moduleName + "Container"],
        borderRadius: 6,
        _web: {
            transition: "0.1s all",
            _hover: {
                opacity: 0.8,
            }
        }
    }),
    chipText: (moduleName) => ({
        fontSize:11,
        color: colors[moduleName],
        fontFamily: fonts.body,
        paddingLeft:5
    }),
    timeText: {
        fontSize: 11,
        color: colors.textSecondary,
        marginTop: 2,
    },
    deletedBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 6,
    },
    deletedBadgeInner: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: colors.errorContainer || colors.error + '12',
        borderRadius: 6,
    },
    deletedBadgeText: {
        fontSize: 11,
        color: colors.error,
        fontFamily: fonts.body,
    },
    detailsCard: {
        marginTop: 8,
        padding: 12,
        backgroundColor: colors.surface,
        borderRadius: 8,
        width: 'fit-content',
    },
    detailsText: {
        fontSize: 12,
        color: colors.textSecondary,
        fontFamily: fonts.body,
        lineHeight: 18,
    },
    footer: {
        paddingVertical: 20,
        alignItems: 'center',
    },
    empty: {
        paddingVertical: 60,
        alignItems: 'center',
        paddingHorizontal: 32,
    },
    emptyText: {
        fontSize: 14,
        color: colors.textSecondary,
        textAlign: 'center',
    },
}));

export default ActivityFeed;
